import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import Spinner from "~/components/spinner";
import { projectCharges, projectChargeIdParam } from "~/routes";
import { useBreadcrumbs } from "~/main-layout/BreadcrumbProvider";
import { useChargeEditorHost } from "./ChargeEditorHost";
import { ChargeEditor } from "./ChargeEditor";

type ChargeEditorRouteParams = {
  [projectChargeIdParam]: string;
};

/**
 * Editor-side, reusable three-state machine. Reads `:projectChargeId` and owns
 * it, so the form's `onSaved` is argument-free.
 *
 *   host loading           → spinner
 *   ready + resolve hit     → render the editor page for that charge
 *   ready + resolve miss    → dismiss() (anti-corruption; a miss is a normal,
 *                             graceful outcome, never a null/foreign charge to
 *                             the editor)
 *
 * The editor's breadcrumb trail is owned here, keyed on the URL id, so the
 * elevated chrome shows the trail immediately — during the loading spinner and
 * a cold load — not only once the charge resolves.
 */
export const ChargeEditorOutlet: React.FC = () => {
  const params = useParams<ChargeEditorRouteParams>();
  const id = Number(params[projectChargeIdParam]);

  const host = useChargeEditorHost();

  useBreadcrumbs(
    [
      { text: "Project Charges", to: projectCharges.path },
      { text: `Project Charge ${id}` },
    ],
    [id]
  );

  const charge = host.status === "ready" ? host.resolve(id) : undefined;
  // A miss (not in the set) and a malformed resolve (no project to seed the
  // form) are both anti-corruption dismissals: the editor never receives a
  // null or unusable charge.
  const usable = charge && charge.project;
  const bounce = host.status === "ready" && !usable;

  useEffect(() => {
    if (bounce) {
      host.dismiss();
    }
  }, [bounce]);

  if (host.status === "loading") {
    return <Spinner open />;
  }

  if (!usable) {
    // ready + miss/malformed: the effect above dismisses; render nothing meanwhile.
    return <></>;
  }

  return (
    <ChargeEditor
      charge={charge}
      onSaved={() => {
        host.absorbSaved(id);
        host.dismiss();
      }}
      onCancel={() => host.dismiss()}
    />
  );
};
